import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { APP_ROUTES } from '../../../utils/route.constant';
import { RouterService } from '../../../services/router/router.service';
import { Subject, takeUntil } from 'rxjs';
import { MessagesService } from '../../../services/messages/messages.service';
import { emailValidator } from '../../../validators/email/email.validator';
import { ApiContact, IPhone } from '../../pages/profile/pages/edit-contact-information/edit-contact-information.interface';
import { CONTACT_INFO_PHONE_NUM_REQ, 
    PHONE_TYPE_OPTIONS, 
    PHONE_VALUE_HOME_FROM_API, 
    PHONE_VALUE_MOBILE_FROM_API, 
    PHONE_VALUE_MOBILE_LABEL, PHONE_VALUE_OFFICE_FROM_API } from '../../pages/profile/profile.constant';
import { DataService } from '../../../services/data/data.service';
import { PreferencesService } from '../../../services/preferences/preferences.service';
import { JobService } from '../../../services/job/job.service';
import { IContact, IContractData, IPrimaryCustomerPhone } from '../../../interfaces/IJob';
import { IMessage } from '../../../services/messages/messages.interface';
import { ProfileService } from '../../pages/profile/profile.service';
import { customPhoneValidator, customValidatorForPhoneList } from '../../../validators/phoneList/phoneList.validator';
import { CONTACT_INFO_FETCH_ERROR_MESSAGE, nonDigitRegex } from '../../../utils/constants';
import { UtilitiesService } from '../../../services/utilities/utilities.service';
import { PROFILE_CONTACT_INFORMATION_UPDATE, PROFILE_PAGE, SR_PM_INIT_FLOW_REVIEW_PAGE } from '../../../services/ga4/ga4.constant';
import { Ga4Service } from '../../../services/ga4/ga4.service';
import { trackByIndex } from '../../../utils/helper';

@Component({
  selector: 'app-update-contact-information',
  templateUrl: './update-contact-information.component.html',
})

export class UpdateContactInformationComponent implements OnDestroy, OnInit {
    @Input() isReviewPage: boolean = false;
    @Input() isClaimCreditPage: boolean = false;
    @Output() isEditMode: EventEmitter<boolean> = new EventEmitter();
    @Output() editContactInfoResponse: EventEmitter<boolean> = new EventEmitter();
    cleaner$ = new Subject<void>();
    isLoading = false;
    contractData: IContractData | null = null;
    contactInformationForm = new FormGroup({
        phoneValueMOBILE: new FormControl ('', [customPhoneValidator(), customValidatorForPhoneList(PHONE_VALUE_MOBILE_FROM_API)]),
        phoneValueHOME1: new FormControl ('', [customPhoneValidator(), customValidatorForPhoneList(PHONE_VALUE_HOME_FROM_API)]),
        phoneValueOFFICE1: new FormControl ('', [customPhoneValidator(), customValidatorForPhoneList(PHONE_VALUE_OFFICE_FROM_API)]),
        preferred: new FormControl ('', Validators.required),
        email: new FormControl ('', [Validators.required, emailValidator()])
    }); 
    contactTypes = PHONE_TYPE_OPTIONS.map(t =>
        t.value === PHONE_VALUE_MOBILE_FROM_API ? { ...t, label: PHONE_VALUE_MOBILE_LABEL } : { ...t });
    phoneNumbersReq = CONTACT_INFO_PHONE_NUM_REQ;
    contactInfoPageLocation:string = '';

    primaryEmail: string = '';
    phoneList!:IPrimaryCustomerPhone[];
    editMode = true;
    highlightInput = false;
    hideCancel = false;
    messages: IMessage[] = [];
    messagesAvailable = false;
    trackByIndex = trackByIndex;
    constructor(private routerService: RouterService, 
        private preferenceService: PreferencesService, 
        private messagesService: MessagesService,
        private jobService: JobService,
        private profileService: ProfileService,
        private dataService: DataService,
        private utilService: UtilitiesService,
        private gaService: Ga4Service) {
    }
  
    ngOnInit(): void {
        this.dataService.contractData$.pipe(takeUntil(this.cleaner$)).subscribe({
            next: (contractData) => {
                if (!contractData) { 
                    this.routerService.goto(APP_ROUTES.DASHBOARD);
                    return; 
                }
                this.contractData = contractData;
                this.initForm();
                this.isReviewPage && this.initialAssignment(this.contractData.contact);
            }
        });

        this.dataService.messagesToDisplay$
            .pipe(takeUntil(this.cleaner$))
            .subscribe({
                next: (message) => {
                this.messages = message;
                this.messagesAvailable = this.messages.length > 0;
                },
            });
        

        // clear the errors when the phone type changes
        this.contactInformationForm.controls.preferred.valueChanges.pipe(takeUntil(this.cleaner$)).subscribe({
            next: () => {
                this.contactInformationForm.controls.phoneValueMOBILE.updateValueAndValidity();
                this.contactInformationForm.controls.phoneValueHOME1.updateValueAndValidity();
                this.contactInformationForm.controls.phoneValueOFFICE1.updateValueAndValidity();
            }
        });
        this.contactInformationForm.valueChanges.pipe(takeUntil(this.cleaner$)).subscribe({
            next: () => {
                this.highlightInput = false;
            }
        });

        if (this.isReviewPage){
            this.contactInfoPageLocation = SR_PM_INIT_FLOW_REVIEW_PAGE;
        }
    }
  
    navigateBack() {
        if (this.isReviewPage) {
            this.editContactInformation(false);
        } else {            
            this.routerService.goto(APP_ROUTES.PROFILE);
        }
    }

    initForm(): void {
        if (!this.contractData) { return; }
        this.contactInformationForm.patchValue({
            email: this.contractData.baseContract?.primaryCustomerEmail ?? ''
        });
        const list = this.contractData.contact?.phoneList ?? [];
        if (!list.length) {
            return;
        }
        const primaryPhone = list.find((phone) => phone.primaryFlag === 'Y');
        const phoneValueMOBILEelem = list.find((phone) => phone.phoneType === PHONE_VALUE_MOBILE_FROM_API);
        const phoneValueHOME1elem = list.find((phone) => phone.phoneType === PHONE_VALUE_HOME_FROM_API);
        const phoneValueOFFICE1elem = list.find((phone) => phone.phoneType === PHONE_VALUE_OFFICE_FROM_API);
        this.contactInformationForm.patchValue({
            preferred: primaryPhone && primaryPhone.phoneType ? primaryPhone.phoneType : '',
            phoneValueMOBILE: phoneValueMOBILEelem ? phoneValueMOBILEelem.phoneNumber : '',
            phoneValueHOME1: phoneValueHOME1elem ? phoneValueHOME1elem.phoneNumber : '',
            phoneValueOFFICE1: phoneValueOFFICE1elem ? phoneValueOFFICE1elem.phoneNumber : ''
        });
    }

    fetchContractData(contractNumber: string): void {
        if (!contractNumber) return;
        this.isLoading = true;
        this.jobService.getContractData(contractNumber)
        .pipe(takeUntil(this.cleaner$))
        .subscribe({
            next: () => { 
                this.isLoading = false;
                if (this.isReviewPage) {
                    this.hideCancel = false;
                    this.editContactInformation(false);
                } else if(this.isClaimCreditPage) {
                    this.dataService.claimCreditInfoUpdateStatus$.next(true);
                    this.editContactInfoResponse.emit(true);
                } else {
                    this.profileService.isProfileUpdated$.next(true);
                    this.routerService.goto(APP_ROUTES.PROFILE);
                }            
            },
            error: (err) => { 
            this.dataService.contractData$.next(null);
                if(this.isClaimCreditPage) {
                    this.editContactInfoResponse.emit(false);
                } else {
                    const errorMessage: IMessage = {
                    severity: 'error',
                    text: err?.messages || CONTACT_INFO_FETCH_ERROR_MESSAGE,
                    icon: 'pi pi-ban'
                    };
                    this.messagesService.addMessages(errorMessage);
                }
                this.isLoading = false;
            }
        });
    }

    checkForValidMobileNumber(): void {
        this.isLoading = true;
        const mobileNumber = this.contactInformationForm.value.phoneValueMOBILE;
        if (!mobileNumber) {
            this.saveContactInformation();
            return;
        }

        // cleared the space and special characters from the mobile number
        // before sending to the API
        this.preferenceService.getMobileNumberValidity(mobileNumber.replace(/\D/g, ""))
            .pipe(takeUntil(this.cleaner$))
            .subscribe({
                next: (validationResponse) => {
                    if (validationResponse && validationResponse.successFlag === 'Y') {
                        if (validationResponse.response) {
                            this.saveContactInformation();
                        } else {
                            this.contactInformationForm.controls.phoneValueMOBILE.setErrors({ invalid: true });
                            this.contactInformationForm.controls.phoneValueMOBILE.markAsTouched();
                            this.contactInformationForm.controls.phoneValueMOBILE.markAsDirty();
                            this.isLoading = false;
                        }
                        
                    }
                },
                error: () => {
                    this.contactInformationForm.controls.phoneValueMOBILE.setErrors({ invalid: true });
                    this.isLoading = false;
                }
            });
    }

    saveContactInformation() {
        if (!this.contactInformationForm.valid) {
            this.isLoading = false;
            this.contactInformationForm.markAllAsTouched();
            return;
        }
        if (this.contractData) {
            this.gaService.buttonClickEvent(PROFILE_PAGE,PROFILE_CONTACT_INFORMATION_UPDATE);
            this.isLoading = true;
            const payload: ApiContact = {
                contractNumber: this.contractData.baseContract.contractNumber,
                emailAddress: this.contactInformationForm.value.email || '',
                phoneList: this.setPhoneListForApi(this.contactInformationForm),
            };
            this.preferenceService.saveContactInfoForProfile(this.contractData.baseContract.contractNumber, payload)
                .pipe(takeUntil(this.cleaner$))
                .subscribe({
                    next: (response: ApiContact) => {
                        this.isLoading = false;
                        if (response.successFlag === 'Y' && this.contractData) {
                            this.fetchContractData(this.contractData.baseContract.contractNumber);
                        } else {
                            this.displayError();
                        }
                    }, error: (err) => {
                        this.isLoading = false;
                        if (err) {
                            this.displayError(err?.messages);
                        }
                    }
                });
        }
    }

    setPhoneListForApi(contactForm: FormGroup): IPhone[] {
        return [
        {
            areaCode: null,
            extention: null,
            phoneNumber: (contactForm.value.phoneValueHOME1 || '').replace(nonDigitRegex,''),
            phoneType: PHONE_VALUE_HOME_FROM_API,
            primaryFlag: this.setPrimaryFlagForPhone(PHONE_VALUE_HOME_FROM_API, contactForm.value.preferred)	
        },
        { 
            areaCode: null,
            extention: null,
            phoneNumber: (contactForm.value.phoneValueOFFICE1 || '').replace(nonDigitRegex,''),
            phoneType: PHONE_VALUE_OFFICE_FROM_API,
            primaryFlag: this.setPrimaryFlagForPhone(PHONE_VALUE_OFFICE_FROM_API, contactForm.value.preferred)				
        },
        { 
            areaCode: null,
            extention: null,
            phoneNumber: (contactForm.value.phoneValueMOBILE || '').replace(nonDigitRegex,''),
            phoneType: PHONE_VALUE_MOBILE_FROM_API,
            primaryFlag: this.setPrimaryFlagForPhone(PHONE_VALUE_MOBILE_FROM_API, contactForm.value.preferred)						
        }
        ];
    }

    setPrimaryFlagForPhone(phoneType: string, primaryPhoneType: string): 'Y' | 'N' {
        return phoneType === primaryPhoneType ? 'Y' : 'N';
    }

    formatPhone(phoneType:string): string {
        const type = this.contactTypes?.find(type => type.value === phoneType)?.label;
        return `${type} :`;
    }

    editContactInformation(state: boolean) {
        this.editMode = state;
        this.isEditMode.emit(state);
    }
    
    initialAssignment(contact: IContact): void {
        this.phoneList = contact?.phoneList || [];
        this.primaryEmail = contact?.emailAddress || '';
        if (this.phoneList.length && this.primaryEmail) {
            this.editContactInformation(false);
        } else {
            this.highlightInput = true;
            this.hideCancel = true;
            this.editContactInformation(true);
            this.contactInformationForm.markAllAsTouched();
            this.contactInformationForm.get('email')?.markAsDirty();
        }
    }

    anyContactNumberPresent(): boolean {
        const { phoneValueMOBILE, phoneValueHOME1, phoneValueOFFICE1 } = this.contactInformationForm.value;
        return Boolean(phoneValueMOBILE || phoneValueHOME1 || phoneValueOFFICE1);
    }

    displayError(msg?: string): void {
        const contactNumberFormatted = this.contractData && this.utilService.formatPhone(this.utilService.getContactNumber(this.contractData?.baseContract));
        const errorMessage: IMessage = {
            severity: 'error',
            text: msg || `Oops, there was an error. Please try again or call us at ${contactNumberFormatted || ''} for assistance.`,
            icon: 'pi pi-ban'
        };
        this.messagesService.addMessages(errorMessage);
    }

    sanitizeFormControl(event: Event, controlName: string): void {
        if (!(event.target instanceof HTMLInputElement)) {
            return;
        }
        const input = event.target;
      
        // Keep only digits, and limit to 10 characters
        const numericValue = input.value.replace(/\D/g, '').slice(0, 10);
      
        // Update the visible input value
        input.value = numericValue;
      
        // Update the Reactive FormControl value without triggering unnecessary events
        this.contactInformationForm.get(controlName)?.setValue(numericValue, { emitEvent: false });
    }

    setButtonName(type: string): string {
        return `Customer_${type}_Phone_Number_Click`;
    }

    ngOnDestroy(): void {
        this.cleaner$.next();
        this.cleaner$.complete();
    }
}
